import {computed, type ComputedRef, getCurrentInstance, onMounted, onUnmounted, ref} from 'vue'

interface ScreenSize {
    isMobile: ComputedRef<boolean>
    isTablet: ComputedRef<boolean>
    isDesktop: ComputedRef<boolean>
    isMobileOrTablet: ComputedRef<boolean>
    isTabletOrDesktop: ComputedRef<boolean>
    isXs: ComputedRef<boolean>
    isSm: ComputedRef<boolean>
    isMd: ComputedRef<boolean>
    isLg: ComputedRef<boolean>
    isXl: ComputedRef<boolean>
    is2xl: ComputedRef<boolean>
    windowWidth: number
    breakpoints: {
        mobile: number
        tablet: number
        desktop: number
    }
}

export function useScreenSize(): ScreenSize {
    const windowWidth = ref(window.innerWidth)
    const instance = getCurrentInstance()

    // Breakpoints - you could make these configurable parameters if needed
    const breakpoints = {
        mobile: 768,    // 0 - 767
        tablet: 1280,   // 768 - 1023
        desktop: 1280   // 1024+
    }

    const tailwindBreakpoints = {
        xs: 0,     // 0 - 639
        sm: 640,   // 640 - 767
        md: 768,   // 768 - 1023
        lg: 1024,  // 1024 - 1279
        xl: 1280,  // 1280 - 1535
        '2xl': 1536// 1536+
    };

    const updateWindowWidth = () => {
        windowWidth.value = window.innerWidth
    }

    // Only attach lifecycle hooks if we're inside a component
    if (instance) {
        onMounted(() => {
            window.addEventListener('resize', updateWindowWidth)
            updateWindowWidth()
        })

        onUnmounted(() => {
            window.removeEventListener('resize', updateWindowWidth)
        })
    } else {
        updateWindowWidth()
    }

    const isLargerDefaultFont = computed(() => {
        const fsize = Number(window.getComputedStyle(document.documentElement).fontSize.replace('px', ''));
        return (fsize > 16)
    })
    const isMobile = computed(() => windowWidth.value < breakpoints.mobile)
    const isTablet = computed(() => {
        if (isLargerDefaultFont.value && !isMobile.value) return true
        return windowWidth.value >= breakpoints.mobile && windowWidth.value < breakpoints.tablet
    })
    const isDesktop = computed(() => {
        if (isLargerDefaultFont.value) return false
        return windowWidth.value >= breakpoints.desktop
    })
    const isMobileOrTablet = computed(() => isMobile.value || isTablet.value)
    const isTabletOrDesktop = computed(() => isTablet.value || isDesktop.value)

    const isXs = computed(() => windowWidth.value < tailwindBreakpoints.sm);
    const isSm = computed(() => windowWidth.value >= tailwindBreakpoints.sm && windowWidth.value < tailwindBreakpoints.md);
    const isMd = computed(() => windowWidth.value >= tailwindBreakpoints.md && windowWidth.value < tailwindBreakpoints.lg);
    const isLg = computed(() => windowWidth.value >= tailwindBreakpoints.lg && windowWidth.value < tailwindBreakpoints.xl);
    const isXl = computed(() => windowWidth.value >= tailwindBreakpoints.xl && windowWidth.value < tailwindBreakpoints['2xl']);
    const is2xl = computed(() => windowWidth.value >= tailwindBreakpoints['2xl']);

    return {
        isMobile,
        isTablet,
        isMobileOrTablet,
        isTabletOrDesktop,
        isDesktop,
        isXs,
        isSm,
        isMd,
        isLg,
        isXl,
        is2xl,
        windowWidth: windowWidth.value,
        breakpoints
    }
}
